import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Component, ErrorInfo, ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught error:", error, errorInfo);
  }

  public render() {
    if (this.state.hasError) {
      return <ErrorFallback error={this.state.error} />;
    }

    return this.props.children;
  }
}

const ErrorFallback = ({ error }: { error: Error | null }) => {
  const navigate = useNavigate();
  const { t } = useTranslation("translation");

  const handleRetry = () => {
    window.location.reload();
  };

  const handleLogout = () => {
    navigate("/auth/login");
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-4">
      <div className="text-center">
        <h1 className="text-2xl font-bold mb-4">{t("error.title")}</h1>
        <p className="text-gray-600 mb-6">{t("error.message")}</p>
        {error && (
          <pre className="bg-gray-100 p-4 rounded mb-6 text-sm">
            {error.message}
          </pre>
        )}
        <div className="space-x-4">
          <button
            onClick={handleRetry}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            {t("error.retry")}
          </button>
          <button
            onClick={handleLogout}
            className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
          >
            {t("error.logout")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ErrorBoundary;
